import { validateManifest } from "./kernel";
import type { LocalAppManifest } from "./contracts";

export const APP_PACKAGE_VERSION = 1 as const;

type AppPackageBody = {
  kind: "browser-local-app-package";
  packageVersion: typeof APP_PACKAGE_VERSION;
  createdAt: string;
  manifest: LocalAppManifest;
  entrypoint: {
    path: string;
    trust: "trusted-origin";
    executable: false;
  };
};

export type LocalAppPackage = AppPackageBody & {
  checksum: string;
};

export type InstalledAppPackage = {
  id: string;
  status: "pending-trust" | "installed" | "migration-required";
  importedAt: string;
  package: LocalAppPackage;
};

export interface AppPackageStore {
  list(): Promise<InstalledAppPackage[]>;
  get(id: string): Promise<InstalledAppPackage | undefined>;
  install(appPackage: LocalAppPackage): Promise<InstalledAppPackage>;
  approve(id: string): Promise<InstalledAppPackage>;
  migrate(id: string, appPackage: LocalAppPackage): Promise<InstalledAppPackage>;
}

export class AppPackageError extends Error {
  constructor(
    public readonly code: "invalid_package" | "unsupported_package" | "manifest_invalid",
    message: string,
  ) {
    super(message);
    this.name = "AppPackageError";
  }
}

export async function exportAppPackage(manifest: LocalAppManifest): Promise<Uint8Array> {
  const body: AppPackageBody = {
    kind: "browser-local-app-package",
    packageVersion: APP_PACKAGE_VERSION,
    createdAt: new Date().toISOString(),
    manifest: structuredClone(manifest),
    entrypoint: {
      path: manifest.path,
      trust: "trusted-origin",
      executable: false,
    },
  };
  const appPackage: LocalAppPackage = { ...body, checksum: await checksum(body) };
  return new TextEncoder().encode(JSON.stringify(appPackage));
}

export async function importAppPackage(bytes: Uint8Array): Promise<LocalAppPackage> {
  let value: unknown;
  try {
    value = JSON.parse(new TextDecoder().decode(bytes));
  } catch {
    throw new AppPackageError("invalid_package", "App package is not valid JSON.");
  }
  if (!value || typeof value !== "object") {
    throw new AppPackageError("invalid_package", "App package must be an object.");
  }
  const candidate = value as Partial<LocalAppPackage>;
  if (candidate.kind !== "browser-local-app-package" || candidate.packageVersion !== APP_PACKAGE_VERSION) {
    throw new AppPackageError("unsupported_package", "App package version is not supported.");
  }
  if (!candidate.manifest || !candidate.entrypoint || typeof candidate.checksum !== "string" || typeof candidate.createdAt !== "string") {
    throw new AppPackageError("invalid_package", "App package envelope is incomplete.");
  }
  try {
    validateManifest(candidate.manifest);
  } catch (error) {
    throw new AppPackageError("manifest_invalid", error instanceof Error ? error.message : "App manifest is invalid.");
  }
  if (
    candidate.entrypoint.trust !== "trusted-origin" ||
    candidate.entrypoint.executable !== false ||
    candidate.entrypoint.path !== candidate.manifest.path
  ) {
    throw new AppPackageError("invalid_package", "App package entrypoint trust metadata is invalid.");
  }
  const body: AppPackageBody = {
    kind: candidate.kind,
    packageVersion: candidate.packageVersion,
    createdAt: candidate.createdAt,
    manifest: candidate.manifest,
    entrypoint: candidate.entrypoint,
  };
  if (candidate.checksum !== await checksum(body)) {
    throw new AppPackageError("invalid_package", "App package checksum does not match.");
  }
  return candidate as LocalAppPackage;
}

export class MemoryAppPackageStore implements AppPackageStore {
  private readonly packages = new Map<string, InstalledAppPackage>();

  async list(): Promise<InstalledAppPackage[]> {
    return [...this.packages.values()].map((value) => structuredClone(value));
  }

  async get(id: string): Promise<InstalledAppPackage | undefined> {
    const value = this.packages.get(id);
    return value ? structuredClone(value) : undefined;
  }

  async install(appPackage: LocalAppPackage): Promise<InstalledAppPackage> {
    const previous = this.packages.get(appPackage.manifest.id);
    const value: InstalledAppPackage = {
      id: appPackage.manifest.id,
      status: previous && (previous.package.manifest.version !== appPackage.manifest.version || previous.package.manifest.dataSchemaVersion !== appPackage.manifest.dataSchemaVersion) ? "migration-required" : "pending-trust",
      importedAt: new Date().toISOString(),
      package: structuredClone(appPackage),
    };
    this.packages.set(value.id, value);
    return structuredClone(value);
  }

  async approve(id: string): Promise<InstalledAppPackage> {
    const value = this.packages.get(id);
    if (!value) throw new AppPackageError("invalid_package", `Installed app descriptor ${id} was not found.`);
    const approved = { ...value, status: "installed" as const };
    this.packages.set(id, approved);
    return structuredClone(approved);
  }

  async migrate(id: string, appPackage: LocalAppPackage): Promise<InstalledAppPackage> {
    const value = this.packages.get(id);
    if (!value) throw new AppPackageError("invalid_package", `Installed app descriptor ${id} was not found.`);
    const migrated: InstalledAppPackage = { ...value, package: structuredClone(appPackage), status: "installed" };
    this.packages.set(id, migrated);
    return structuredClone(migrated);
  }
}

export class IndexedDbAppPackageStore implements AppPackageStore {
  private constructor(private readonly database: IDBDatabase) {}

  static async open(): Promise<IndexedDbAppPackageStore> {
    if (typeof indexedDB === "undefined") throw new Error("IndexedDB is unavailable in this browser context.");
    const database = await new Promise<IDBDatabase>((resolve, reject) => {
      const request = indexedDB.open("browser-local-app-packages", 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains("installed-apps")) {
          request.result.createObjectStore("installed-apps", { keyPath: "id" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("App package database open failed."));
      request.onblocked = () => reject(new Error("App package database upgrade is blocked."));
    });
    return new IndexedDbAppPackageStore(database);
  }

  async list(): Promise<InstalledAppPackage[]> {
    return this.transaction("readonly", (store) => requestToPromise(store.getAll()));
  }

  async get(id: string): Promise<InstalledAppPackage | undefined> {
    return this.transaction("readonly", (store) => requestToPromise(store.get(id)));
  }

  async install(appPackage: LocalAppPackage): Promise<InstalledAppPackage> {
    const previous = await this.get(appPackage.manifest.id);
    const value: InstalledAppPackage = {
      id: appPackage.manifest.id,
      status: previous && (previous.package.manifest.version !== appPackage.manifest.version || previous.package.manifest.dataSchemaVersion !== appPackage.manifest.dataSchemaVersion) ? "migration-required" : "pending-trust",
      importedAt: new Date().toISOString(),
      package: appPackage,
    };
    await this.transaction("readwrite", (store) => requestToPromise(store.put(value)));
    return value;
  }

  async approve(id: string): Promise<InstalledAppPackage> {
    const value = await this.get(id);
    if (!value) throw new AppPackageError("invalid_package", `Installed app descriptor ${id} was not found.`);
    const approved: InstalledAppPackage = { ...value, status: "installed" };
    await this.transaction("readwrite", (store) => requestToPromise(store.put(approved)));
    return approved;
  }

  async migrate(id: string, appPackage: LocalAppPackage): Promise<InstalledAppPackage> {
    const value = await this.get(id);
    if (!value) throw new AppPackageError("invalid_package", `Installed app descriptor ${id} was not found.`);
    const migrated: InstalledAppPackage = { ...value, package: appPackage, status: "installed" };
    await this.transaction("readwrite", (store) => requestToPromise(store.put(migrated)));
    return migrated;
  }

  private transaction<T>(mode: IDBTransactionMode, operation: (store: IDBObjectStore) => Promise<T>): Promise<T> {
    const transaction = this.database.transaction("installed-apps", mode);
    return new Promise<T>((resolve, reject) => {
      let result: T;
      let operationFinished = false;
      transaction.oncomplete = () => {
        if (operationFinished) resolve(result);
      };
      transaction.onerror = () => reject(transaction.error ?? new Error("App package transaction failed."));
      transaction.onabort = () => reject(transaction.error ?? new Error("App package transaction aborted."));
      void operation(transaction.objectStore("installed-apps"))
        .then((value) => {
          result = value;
          operationFinished = true;
        })
        .catch(reject);
    });
  }
}

async function checksum(value: AppPackageBody): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(JSON.stringify(value)));
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("App package request failed."));
  });
}
